import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef, functionalUpdate, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Badge, Button } from "@telia/teddy";
import { useSelector } from "react-redux";
import * as properNameCase from "../../../../../shared/properNameCase";
import { RootState } from "../../../../../models";
import { API_URL_GET_MANUAL_ATTENTION_ORDERS } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import { OrderStateEnum } from "../../types/enums";
import { OrderItem } from "../../types/models";
import { getStatusText, statusVariant } from "./orderStatusUtils";
import { getSalesAgents } from "../../state/selectors/selectors";
import { ManualAttentionType } from "./useOrdersFilterParams";

const PAGE_SIZE = 25;

interface ManualAttentionOrdersPage {
    orders: OrderItem[];
    pageNumber: number;
    pageSize: number;
    totalHits: number;
}

const EMPTY_PAGE: ManualAttentionOrdersPage = { orders: [], totalHits: 0, pageNumber: 0, pageSize: PAGE_SIZE };

const fetchManualAttentionOrders = async (
    manualAttentionType: ManualAttentionType,
    fromDate?: Date | null,
    toDate?: Date | null,
    pageNumber = 0,
    agentId = "",
    search = "",
): Promise<ManualAttentionOrdersPage> => {
    const url = new URL(API_URL_GET_MANUAL_ATTENTION_ORDERS, window.location.href);
    url.searchParams.set("manualAttentionType", manualAttentionType);
    if (fromDate) url.searchParams.set("fromDate", fromDate.toISOString());
    if (toDate) url.searchParams.set("toDate", toDate.toISOString());
    url.searchParams.set("pageNumber", String(pageNumber));
    url.searchParams.set("pageSize", String(PAGE_SIZE));
    if (agentId) url.searchParams.set("agentId", agentId);
    if (search) url.searchParams.set("search", search);
    const response = await retailerFetch(url.toString());
    if (!response.ok) {
        let message = "Kunne ikke hente ordre som krever oppfølging. Prøv igjen senere.";
        try {
            const body = await response.json();
            if (body?.error) message = body.error;
        } catch {
            // ignore parse error
        }
        throw new Error(message);
    }
    return response.json();
};

interface UseManualAttentionOrdersTableOptions {
    agentId: string;
    enabled: boolean;
    endDate: Date | null;
    manualAttentionType: ManualAttentionType;
    onPageIndexChange: (pageIndex: number) => void;
    pageIndex: number;
    search: string;
    startDate: Date | null;
}

const useManualAttentionOrdersTable = ({
    agentId,
    enabled,
    endDate,
    manualAttentionType,
    onPageIndexChange,
    pageIndex,
    search,
    startDate,
}: UseManualAttentionOrdersTableOptions) => {
    const salesAgents = useSelector((state: RootState) => getSalesAgents(state));
    const pagination = { pageIndex, pageSize: PAGE_SIZE };
    const [chatOrderId, setChatOrderId] = useState<number | null>(null);

    const queryKey = [
        "manualAttentionOrders",
        manualAttentionType,
        startDate?.toISOString(),
        endDate?.toISOString(),
        pageIndex,
        agentId,
        search,
    ];

    const {
        data = EMPTY_PAGE,
        isLoading,
        error,
    } = useQuery({
        queryKey,
        queryFn: () => fetchManualAttentionOrders(manualAttentionType, startDate, endDate, pageIndex, agentId, search),
        enabled,
        retry: false,
    });

    const { orders, totalHits } = data;
    const pageCount = totalHits > 0 ? Math.ceil(totalHits / PAGE_SIZE) : 1;

    const columns = useMemo<ColumnDef<OrderItem>[]>(
        () => [
            {
                accessorKey: "orderId",
                id: "ref",
                header: "Ordre-ID",
            },
            {
                accessorKey: "legalResponsibleName",
                id: "legalResponsibleName",
                header: "Kunde",
                cell: ({ getValue }) => <span data-di-mask>{properNameCase.convert(getValue<string>() ?? "")}</span>,
            },
            {
                accessorKey: "orderState",
                id: "status",
                header: "Status",
                cell: ({ getValue }) => {
                    const val = getValue<OrderStateEnum | undefined>();
                    if (!val) return "";
                    return (
                        <Badge variant={statusVariant(val)} hideIcon>
                            {getStatusText(val)}
                        </Badge>
                    );
                },
            },
            {
                accessorKey: "salesAgent",
                id: "salesAgent",
                header: "Selger",
                cell: ({ getValue }) => {
                    const salesAgentId = getValue<string | undefined>();
                    const salesAgent = salesAgents.find((agent) => String(agent.id) === String(salesAgentId));
                    return (
                        <span data-di-mask>
                            {salesAgent ? `${salesAgent.firstName} ${salesAgent.lastName.slice(0, 1)}.` : "Ikke angitt"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "creationDate",
                id: "creationDate",
                header: "Dato",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    if (!val) return "";
                    const d = new Date(val);
                    return isNaN(d.getTime())
                        ? val
                        : d.toLocaleString("nb-NO", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                          });
                },
            },
            {
                id: "actions",
                header: "",
                cell: ({ row }) => {
                    const { orderId } = row.original;
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Button variant="secondary" size="sm" onClick={() => setChatOrderId(orderId)}>
                                Chat
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [salesAgents],
    );

    const table = useReactTable({
        columns,
        data: orders,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        pageCount,
        state: { pagination },
        onPaginationChange: (updater) => onPageIndexChange(functionalUpdate(updater, pagination).pageIndex),
    });

    return {
        chatOrderId,
        columns,
        error,
        isLoading,
        setChatOrderId,
        table,
    };
};

export default useManualAttentionOrdersTable;
